import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ConcentricRings } from '@/components/Motifs';
import { setToken } from '@/lib/auth';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Shield } from 'lucide-react';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!email || !password) return;
    setLoading(true);
    try {
      const res = await api.post('/admin/login', { email, password });
      if (res.data?.token) {
        setToken(res.data.token);
        toast.success('Welcome back.');
        navigate('/admin');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(164,28%,14%)] px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <ConcentricRings size={60} opacity={0.2} />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[hsl(var(--foreground))] mb-2">
            Admin Portal
          </h1>
          <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">
            The Cosmic Synthesist
          </p>
        </div>

        <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.15)] p-6 sm:p-8 shadow-[0_18px_60px_rgba(0,0,0,0.45)]">
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm"
                data-testid="admin-email-input"
                required
              />
            </div>
            <div>
              <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">
                Password
              </label>
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm"
                data-testid="admin-password-input"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium py-6 rounded-lg hover:brightness-[1.03] disabled:opacity-50"
              data-testid="admin-login-button"
            >
              <Shield className="h-4 w-4 mr-2" />
              {loading ? 'Authenticating...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
